/**
 * TutorialOverlay — a dim full-screen overlay + speech-bubble tooltip
 * used to walk a student through the first question.
 *
 * Highlights any game object passed to `spotlight(obj)` by moving its
 * scene depth above the overlay (and restoring on `clearSpotlight()`).
 *
 * Usage:
 *   const tut = new TutorialOverlay(scene);
 *   tut.show();
 *   tut.spotlight([targetBlock]);
 *   tut.showTooltip("Here's a 4 kg weight block.", { x: 480, y: 320 });
 *   ...
 *   tut.hide();
 */
export default class TutorialOverlay {
  constructor(scene) {
    this.scene = scene;
    this.savedDepths = new Map();

    // Full-screen dim layer (renders above everything except spotlighted
    // game objects, which we lift above OVERLAY_DEPTH).
    this.OVERLAY_DEPTH = 100;
    this.SPOTLIGHT_DEPTH = 110;
    this.TOOLTIP_DEPTH = 120;

    this.dim = scene.add
      .rectangle(0, 0, 1280, 720, 0x000000, 0.6)
      .setOrigin(0)
      .setDepth(this.OVERLAY_DEPTH)
      .setVisible(false);
  }

  show() {
    this.dim.setVisible(true);
  }

  hide() {
    this.dim.setVisible(false);
    this.clearSpotlight();
    this.hideTooltip();
  }

  /**
   * Lift the given game objects above the dim overlay so they render
   * in normal colour. For scene-root objects we just bump their depth.
   * For container children, we temporarily reparent to the scene root
   * (preserving world position) so they can sit above the overlay's
   * depth — containers clip their children to their own render order.
   */
  spotlight(objs) {
    this.clearSpotlight();
    objs.forEach(obj => {
      if (obj.parentContainer) {
        const wm = obj.getWorldTransformMatrix();
        const worldX = wm.tx;
        const worldY = wm.ty;
        const parent = obj.parentContainer;
        parent.remove(obj, false);
        this.scene.add.existing(obj);
        obj.x = worldX;
        obj.y = worldY;
        this.savedDepths.set(obj, {
          originalDepth: obj.depth,
          parent,
        });
      } else {
        this.savedDepths.set(obj, { originalDepth: obj.depth });
      }
      obj.setDepth(this.SPOTLIGHT_DEPTH);
    });
  }

  clearSpotlight() {
    const parentsToSort = new Set();
    this.savedDepths.forEach((info, obj) => {
      if (info.parent) {
        // Reparent back, converting world → parent-local.
        const wm = obj.getWorldTransformMatrix();
        const worldX = wm.tx;
        const worldY = wm.ty;
        this.scene.children.remove(obj);
        info.parent.add(obj);
        const local = info.parent.getWorldTransformMatrix().applyInverse(worldX, worldY);
        obj.x = local.x;
        obj.y = local.y;
        parentsToSort.add(info.parent);
      }
      obj.setDepth(info.originalDepth);
    });
    // Re-sort each affected container by depth so the restored object
    // sits back at its original z-layer (e.g. weight behind the bowl).
    parentsToSort.forEach(p => p.sort('depth'));
    this.savedDepths.clear();
  }

  /**
   * Show a tooltip — Athletics-Regular white text with the `arrow`
   * asset placed beside it (left | right | up | down). Position is the
   * tooltip centre. Whole tooltip is nudged 10 px right and 20 px up
   * from the requested anchor; the arrow gently wiggles toward / away
   * from the highlighted object to draw the eye.
   */
  showTooltip(text, { x, y, arrow = 'left' }) {
    this.hideTooltip();

    const label = this.scene.add
      .text(0, 0, text, {
        fontFamily: 'Athletics-Regular',
        fontSize: '28px',
        color: '#FFFFFF',
        wordWrap: { width: 600 },
        align: 'center',
      })
      .setOrigin(0.5, 0.5);

    // No-arrow mode: just plain centred text.
    if (arrow === 'none' || arrow == null) {
      this.tooltip = this.scene.add.container(x, y, [label]).setDepth(this.TOOLTIP_DEPTH);
      return;
    }

    const arrowGap = 18;
    const arrowImg = this.scene.add.image(0, 0, 'arrow').setOrigin(0.5, 0.5);

    // arrow.png points LEFT by default — flip / rotate for other directions
    // and place the arrow on the corresponding side of the text.
    let restX = 0;
    let restY = 0;
    let axis = 'x';
    let wiggle = -10;
    if (arrow === 'left') {
      restX = -label.width / 2 - arrowGap - arrowImg.width / 2;
      arrowImg.x = restX;
      wiggle = -10;
    } else if (arrow === 'right') {
      restX = label.width / 2 + arrowGap + arrowImg.width / 2;
      arrowImg.x = restX;
      arrowImg.setFlipX(true);
      wiggle = 10;
    } else if (arrow === 'up') {
      restY = -label.height / 2 - arrowGap - arrowImg.height / 2;
      arrowImg.y = restY;
      arrowImg.setAngle(90);
      axis = 'y';
      wiggle = -10;
    } else if (arrow === 'down') {
      restY = label.height / 2 + arrowGap + arrowImg.height / 2;
      arrowImg.y = restY;
      arrowImg.setAngle(-90);
      axis = 'y';
      wiggle = 10;
    }

    // Wiggle the arrow back and forth toward the highlighted object.
    this.scene.tweens.add({
      targets: arrowImg,
      [axis]: (axis === 'x' ? restX : restY) + wiggle,
      duration: 450,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });

    this.tooltip = this.scene.add
      .container(x + 20, y - 35, [arrowImg, label])
      .setDepth(this.TOOLTIP_DEPTH);
  }

  hideTooltip() {
    if (this.tooltip) {
      this.tooltip.destroy();
      this.tooltip = null;
    }
  }
}
